import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Input, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import PageHeader from "../../components/common/PageHeader";
import pushAtrasadosService, { PushAtrasadosRow } from "../../services/pushAtrasados";

const { Text } = Typography;

const PAGE_SIZE = 50;

// Classifica o CNJ conforme os padrões de regras configurados e retorna o label
function classifyRule(cnj: string): string {
  if (!cnj) return "-";

  const makeRegex = (pattern: string) => {
    const escaped = pattern.replace(/[-\/\\^$+?.()|[\]{}]/g, "\\$&");
    const regexStr = "^" + escaped.replace(/%/g, ".*") + "$";
    return new RegExp(regexStr);
  };

  const groups: { label: string; patterns: string[] }[] = [
    { label: "1 - DIÁRIO", patterns: ["%.1.%", "%.2.%", "%.3.%", "%.6.%", "%.7.%", "%.9.%"] },
    {
      label: "2 - SEMANAL",
      patterns: ["%.5.01.%", "%.4.04.%", "%.4.02.%", "%.5.13.%", "%.8.23.%", "%.8.24.%", "%.8.05.%", "%.5.03.%", "%.5.05.%", "%.5.09.%", "%.8.12.%", "%.8.21.%", "%.8.09.%", "%.8.25.%"],
    },
    { label: "3 - SOLUCIONARE - DIÁRIO", patterns: ["%.8.08.%", "%.8.18.%", "%.8.20.%", "%.8.27.%"] },
    {
      label: "4 - OSCILANTE",
      patterns: ["%.4.00.%", "%.4.01.%", "%.4.03.%", "%.4.05.%", "%.4.06.%", "%.5.00.%", "%.5.02.%", "%.5.04.%", "%.5.06.%", "%.5.07.%", "%.5.08.%", "%.5.10.%", "%.5.11.%", "%.5.12.%", "%.5.14.%", "%.5.15.%", "%.5.16.%", "%.5.17.%", "%.5.18.%", "%.5.19.%", "%.5.20.%", "%.5.21.%", "%.5.22.%", "%.5.23.%", "%.5.24.%"],
    },
    {
      label: "5 - DIÁRIO",
      patterns: ["%.8.26.%", "%.8.28.%", "%.8.00.%", "%.8.01.%", "%.8.02.%", "%.8.03.%", "%.8.04.%", "%.8.06.%", "%.8.07.%", "%.8.10.%", "%.8.11.%", "%.8.13.%", "%.8.14.%", "%.8.15.%", "%.8.16.%", "%.8.17.%", "%.8.19.%", "%.8.22.%"],
    },
  ];

  const target = String(cnj);
  for (const g of groups) {
    for (const p of g.patterns) {
      if (makeRegex(p).test(target)) return g.label;
    }
  }
  return "-";
}

// Formata segundos restantes em texto legível (ex: "23h 12min")
function formatTtl(seconds: number | null): string {
  if (seconds === null || seconds < 0) return "-";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

const PushAtrasadosPage: React.FC = () => {
  const [data, setData] = useState<PushAtrasadosRow[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState<boolean>(false);
  const [cacheExpiresIn, setCacheExpiresIn] = useState<number | null>(null);

  // Filtros mantidos em ref para que load() sempre acesse o valor atual
  // sem precisar ser recriado a cada render (evita loops de useEffect).
  const [filterPushId, setFilterPushId] = useState<string>("");
  const [filterDemandId, setFilterDemandId] = useState<string>("");
  const [filterCnj, setFilterCnj] = useState<string>("");
  const [filterLogado, setFilterLogado] = useState<string>("");

  const filtersRef = useRef({ filterPushId, filterDemandId, filterCnj, filterLogado });

  // Mantém a ref sempre atualizada com o estado atual dos filtros
  useEffect(() => {
    filtersRef.current = { filterPushId, filterDemandId, filterCnj, filterLogado };
  }, [filterPushId, filterDemandId, filterCnj, filterLogado]);

  const load = useCallback(async (
    p: number,
    forceRefresh = false,
    overrideFilters?: Record<string, string>,
  ) => {
    // Usa overrideFilters quando fornecido (ex: limpar filtros), senão lê a ref
    const f = overrideFilters !== undefined ? overrideFilters : {
      pushId:   filtersRef.current.filterPushId.trim(),
      demandid: filtersRef.current.filterDemandId.trim(),
      cnj:      filtersRef.current.filterCnj.trim(),
      logado:   filtersRef.current.filterLogado,
    };

    // Remove entradas vazias para não poluir a query string
    const activeFilters: Record<string, string> = {};
    Object.entries(f).forEach(([k, v]) => { if (v) activeFilters[k] = v; });

    setErrorMessage(null);
    setLoading(true);
    try {
      const res = await pushAtrasadosService.list(p, forceRefresh, Object.keys(activeFilters).length ? activeFilters : undefined);
      if (res && res.success) {
        const classified = (res.data || []).map((row: PushAtrasadosRow) => {
          const cnjVal = (row as any).cnj || "";
          return { ...(row as any), regra: classifyRule(String(cnjVal)) };
        });
        setData(classified);
        setTotal(res.total || 0);
        setFromCache(res.fromCache ?? false);
        setCacheExpiresIn(res.cacheExpiresIn ?? null);
      } else {
        setData([]);
        setTotal(0);
        setErrorMessage("Nenhum dado retornado do servidor.");
      }
    } catch (err: any) {
      console.error("Erro ao buscar pushes atrasados", err);
      const status = err?.response?.status;
      if (status === 404) {
        setErrorMessage("Endpoint /push/atrasados não encontrado (404). Verifique se o backend está rodando.");
      } else if (status === 401) {
        setErrorMessage("Não autorizado (401). Verifique autenticação.");
      } else if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        setErrorMessage("Tempo de resposta esgotado. Verifique a conexão com o banco e os logs do backend.");
      } else if (status === 504 || /504|tempo limite/i.test(err?.response?.data?.message || "")) {
        setErrorMessage("O banco de dados demorou mais de 60s para responder. Tente novamente em alguns instantes.");
      } else if (status === 500) {
        setErrorMessage("Erro interno do servidor (500). Verifique os logs do backend.");
      } else {
        setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao buscar dados");
      }
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Carrega página 1 na montagem somente se houver filtros ativos.
  // Caso contrário, a tela abrirá em branco até o usuário clicar "Atualizar" ou "Filtrar".
  useEffect(() => {
    const hasFilters = [filterPushId, filterDemandId, filterCnj, filterLogado]
      .some((v) => !!v && String(v).trim() !== "");
    if (hasFilters) {
      load(1);
    }
    // se nenhum filtro, não buscar automaticamente — aguardará clique em "Atualizar" ou "Filtrar"
  }, [load, filterPushId, filterDemandId, filterCnj, filterLogado]);

  const handlePageChange = (p: number) => {
    setPage(p);
    load(p, false);
  };

  const handleForceRefresh = () => {
    setPage(1);
    load(1, true);
  };

  const handleFilter = () => {
    setPage(1);
    load(1, false);
  };

  const handleClearFilters = () => {
    setFilterPushId("");
    setFilterDemandId("");
    setFilterCnj("");
    setFilterLogado("");
    setPage(1);
    // Passa filtros vazios explicitamente para ignorar o estado ainda não atualizado
    load(1, false, {});
  };

  const columns = [
    {
      title: "Push ID",
      dataIndex: "pushId",
      key: "pushId",
      render: (_: any, record: PushAtrasadosRow) => {
        const r = record as any;
        const id = record.pushId || r.id || r.pushid || "";
        return (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Text copyable code style={{ marginRight: 8 }}>{String(id || "-")}</Text>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => window.open(`/push-details/${id}`, "_blank")}
            >
              Ver push
            </Button>
          </div>
        );
      },
    },
    {
      title: "Demand ID",
      dataIndex: "demandid",
      key: "demandid",
      render: (_: any, record: PushAtrasadosRow) => {
        const r = record as any;
        return <Text>{String(r.demandid || "-")}</Text>;
      },
    },
    {
      title: "CNJ",
      dataIndex: "cnj",
      key: "cnj",
    },
    {
      title: "Regra",
      dataIndex: "regra",
      key: "regra",
      render: (_: any, record: PushAtrasadosRow) => {
        const r = record as any;
        return r.regra ? <Text>{r.regra}</Text> : <Text type="secondary">-</Text>;
      },
    },
    {
      title: "Criado em",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_: any, record: PushAtrasadosRow) => {
        const val = record.createdAt || (record as any).createdat;
        return val ? new Date(val).toLocaleString() : "-";
      },
    },
    {
      title: "Atraso (dias)",
      dataIndex: "atraso",
      key: "atraso",
    },
    {
      title: "Logado",
      dataIndex: "logado",
      key: "logado",
      render: (_: any, record: PushAtrasadosRow) => {
        const val = !!(record as any).logado;
        return val ? <Text>Sim</Text> : <Text>Não</Text>;
      },
    },
  ];

  return (
    <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <PageHeader title="Monitoramentos Atrasados" />
          <Space align="center">
            {!loading && data.length > 0 && (
              <Tooltip title={fromCache ? `Cache expira em ${formatTtl(cacheExpiresIn)}` : "Dados carregados diretamente do banco"}>
                <Tag color={fromCache ? "blue" : "green"}>
                  {fromCache ? `Cache (expira em ${formatTtl(cacheExpiresIn)})` : "Ao vivo"}
                </Tag>
              </Tooltip>
            )}
            <Button icon={<ReloadOutlined />} onClick={handleForceRefresh} loading={loading}>
              Atualizar
            </Button>
          </Space>
        </div>

        {/* Filtros */}
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Input
            placeholder="Push ID"
            value={filterPushId}
            onChange={(e) => setFilterPushId(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Input
            placeholder="Demand ID"
            value={filterDemandId}
            onChange={(e) => setFilterDemandId(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Input
            placeholder="CNJ"
            value={filterCnj}
            onChange={(e) => setFilterCnj(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Select
            placeholder="Logado"
            value={filterLogado || undefined}
            onChange={(v) => setFilterLogado(v ?? "")}
            style={{ width: 120 }}
            allowClear
          >
            <Select.Option value="sim">Sim</Select.Option>
            <Select.Option value="nao">Não</Select.Option>
          </Select>
          <Button type="primary" onClick={handleFilter}>Filtrar</Button>
          <Button onClick={handleClearFilters}>Limpar</Button>
        </div>

        {errorMessage && (
          <Alert style={{ marginTop: 16 }} type="error" message={errorMessage} />
        )}

        <Table
          style={{ marginTop: 16 }}
          dataSource={data}
          columns={columns}
          rowKey="pushId"
          loading={loading}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showTotal: (t) => `${t} registros`,
            onChange: handlePageChange,
          }}
        />
      </Card>
  );
};

export default PushAtrasadosPage;
